/**
 * @file index.tsx
 *
 * @fileoverview Renders a taxonomy reference type field on an
 * entity as a multiple autocomplete.
 */
import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  startTransition,
} from 'react';
import { useQuery } from '@apollo/client';
import { useIntl } from 'react-intl';
import { Controller, useFormContext } from 'react-hook-form';
import { AppContext } from '~/jobiqo/context/app';
import GET_TAXONOMY_ITEMS from '~/jobiqo/components/Form/Widgets/TaxonomyReference/graphql/queries/taxonomyItems.gql';
import {
  getAllChildrenOfTerm,
  getTaxonomyVocabularies,
} from '~/jobiqo/components/Form/Widgets/TaxonomyReference/taxonomyHelpers';
import { getPlaceholder } from '~/jobiqo/components/Form/helpers/formSettings';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { MultipleAutocomplete } from '~/vcl/atoms/01-forms/05-select/search/multiple';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { useFieldRules } from '../../../hooks/useFieldRules';
import { EntityField } from '~/jobiqo/models/entity';
import { AppContextInterface } from '~/jobiqo/models/app';
import { EntityFieldRules } from '~/jobiqo/models/config';
import { useConfig } from '~/jobiqo/context/config';

// Props used by this component.
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The field definition.
   */
  entityField: EntityField;
  /**
   * The flag that indicates if we should refresh the data from the backend.
   */
  refresh?: any;
  /**
   * The field rules to check if the field is required, hidden or disabled.
   */
  fieldRules?: EntityFieldRules;
}

export const TaxonomyReferenceMultiple = ({
  entityField,
  fieldRules = {},
  refresh,
}: Props) => {
  const appContext: AppContextInterface = useContext(AppContext);
  const { control, getValues, setValue } = useFormContext();
  const values = getValues();
  const { hidden, required, hideSubTreeElements } = useFieldRules(
    entityField.id,
    values,
    fieldRules,
  );
  const { getConfigByName } = useConfig();
  const SALARY_FREE_INPUT_ENABLED: boolean = !!getConfigByName(
    'jobiqo_job_salary_range_free_input',
  )?.value[0];

  const [searchValue, setSearchValue] = useState('');
  const intl = useIntl();
  const tids = values?.[entityField.id]?.map((i) => Number(i.tid)) || [];
  const { loading, error: errorQuery, data, previousData, refetch } = useQuery(
    GET_TAXONOMY_ITEMS,
    {
      variables: {
        vocabularies: getTaxonomyVocabularies(entityField),
        freeTids: tids,
        language: appContext?.locale,
      },
    },
  );

  // Use previousData if data is undefined to avoid flickering when refetching.
  const taxonomyData = data ?? previousData;

  useEffect(() => {
    refetch();
  }, [refresh, refetch]);

  useEffect(() => {
    if (hidden && !!values[entityField.id]) {
      startTransition(() => {
        setValue(entityField.id, []);
      });
    }
  }, [hidden, values, entityField, setValue]);

  useEffect(() => {
    // we have to clear the value of the field if the main element is selected and the rule is now set to hide it
    // (this does not apply to children as that would be too expensive to keep checking for every child of the term)
    if (
      hideSubTreeElements &&
      (hideSubTreeElements.includes(values[entityField.id]) ||
        hideSubTreeElements?.some((i) =>
          values?.[entityField.id]?.map((i) => i.tid)?.includes(i),
        ))
    ) {
      startTransition(() => {
        setValue(entityField.id, []);
      });
    }
  }, [hideSubTreeElements, values, entityField, setValue]);

  const strVocabulariesTerms: typeof taxonomyData.vocabulariesTerms = useMemo(() => {
    if (!taxonomyData) {
      return [];
    }

    // Our multivalue select expects keys as strings so we need to convert them.
    let terms = taxonomyData.vocabulariesTerms.map((i) => {
      // Get the term parent label.
      const parent = i?.parent;
      return {
        ...i,
        name: parent && !!searchValue ? `${i.name} (${parent?.name})` : i.name,
        code: i.code.toString(),
        parent: parent,
      };
    });

    // If we have a hideSubTreeElements we need to remove all children of the term.
    // This should be skipped if nothing is marked to be hidden as this can be an expensive
    // operation on large taxonomies.
    if (hideSubTreeElements) {
      hideSubTreeElements.forEach((hideSubTree) => {
        const allChildren = getAllChildrenOfTerm(hideSubTree.toString(), terms);
        terms = terms.filter((term) => !allChildren.includes(term.code));
      });
    }

    // Prevent duplicate keys in MultipleAutocomplete.
    return Array.from(new Map(terms.map((term) => [term.code, term])).values());
  }, [taxonomyData, searchValue, hideSubTreeElements]);

  const handleInputChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const handleChange = useCallback(
    (values, freeTerms) => {
      startTransition(() => {
        const allItems = [
          ...strVocabulariesTerms,
          ...values
            .filter(
              (i) =>
                !strVocabulariesTerms.some(
                  (j) => j.code === i?.tid?.toString(),
                ),
            )
            .map((i) => ({
              name: i.label,
              code: i?.tid?.toString(),
              parent: i?.parent?.tid,
            })),
        ];

        const fullValues = values
          .map((id) => allItems.find((term) => term.code === id))
          .filter((t) => !!t)
          .map((term) => ({
            tid: Number(term.code),
            label: term.name,
            parent: allItems.find((t) => t.code === term.code)?.parent,
          }));

        setValue(entityField.id, fullValues, {
          shouldValidate: true,
          shouldDirty: true,
        });

        if (freeTerms && freeTerms?.length > 0) {
          setValue(
            `${entityField.id}_free`,
            freeTerms.filter((i) => !!i),
            {
              shouldValidate: true,
              shouldDirty: true,
            },
          );
        }
      });
    },
    [strVocabulariesTerms, setValue, entityField.id],
  );

  if (hidden) {
    return null;
  }

  // Do not render the field if we are still loading and we don't have any data yet.
  if (loading && !taxonomyData) {
    return null;
  }

  if (entityField.id == 'field_job_salary_range' && SALARY_FREE_INPUT_ENABLED) {
    return null;
  }

  if (errorQuery) {
    return (
      <MessageAlert alertStyle="danger" type="assertive">
        Error loading terms for field.
      </MessageAlert>
    );
  }
  return (
    <Controller
      name={entityField.id}
      control={control}
      render={({ field, fieldState: { error } }) => {
        return (
          <>
            <MultipleAutocomplete
              {...field}
              id={field.name}
              selectedMark={intl.formatMessage({
                id: 'multiple_autocomplete_selected_mark',
                defaultMessage: 'Selected',
              })}
              label={entityField.label}
              description={entityField.description}
              placeholder={getPlaceholder(entityField)}
              required={entityField.required || required}
              values={field.value.map((i) => i.tid.toString())}
              items={strVocabulariesTerms}
              hasFreeItems={Object.keys(
                entityField?.settings?.filter(
                  (i) => i.key === 'handler_settings',
                )?.[0]?.value?.target_bundles,
              ).some((key) => key.includes('free_tagging'))}
              freeItemsAddButtonText={intl.formatMessage({
                id: 'multiple_autocomplete_add_button_text',
                defaultMessage: 'Add free term',
              })}
              freeItemsMaxLengthErrorText={intl.formatMessage({
                id: 'multiple_autocomplete_free_term_too_long',
                defaultMessage: 'Term names must be 255 characters or fewer.',
              })}
              onChange={handleChange}
              error={!!error}
              onInputChange={handleInputChange}
            />
            {!!error && <InlineError message={error.message} />}
          </>
        );
      }}
    />
  );
};

export default TaxonomyReferenceMultiple;
